<template>
  <v-card style="height: 100%; max-height: 100%; overflow-y: auto" class="ma-0 pa-0" flat>
    <v-row class="ma-0 pa-0" style="width: 100%;" align="center">
      <v-col cols="auto">
        <span class="report-subtitle">Total Units VS Cost Per Unit</span>
      </v-col>
      <v-col cols="12" class="ma-0 pa-0" style="height: 10px;">
        <v-divider class="mx-12"></v-divider>
      </v-col>
      <v-col cols="12" v-if="!graph_visible">
        <v-skeleton-loader
          light
          :style="{
            height: screenHeight + 'px',
          }"
          type="image,image,image,image"
        ></v-skeleton-loader>
      </v-col>
      <v-col v-if="graph_visible" cols="12" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0" style="height: 100%;">
          <v-col cols="12">
            <div
              :style="{
                height: screenHeight + 'px',
              }"
            >
              <v-chart
                class="performanceChart"
                :option="graph_options"
                :ref="chart_ref"
                :id="chart_id"
                autoresize
              ></v-chart>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import "echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
import {
  PERFORMANCE_AXIS_LABEL,
  PERFORMANCE_AXIS_LINE,
  PERFORMANCE_AXIS_NAME,
  PERFORMANCE_CHART_COLORS,
  PERFORMANCE_LEGEND_TEXT,
  PERFORMANCE_SPLIT_LINE,
} from "./performanceChartStyle.js";

use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);

export default {
  name: "TotalUnitsVsCpu",
  props: [
    "dataset",
    "color_palette",
    "chart_ref",
    "chart_id",
    "chart_update_key",
  ],
  components: {
    VChart,
  },
  provide: {
    [THEME_KEY]: "light",
  },
  data: () => ({
    graph_visible: false,
    graph_data: null,
    screenHeight: window.innerHeight * 0.55,
  }),
  mounted() {
    this.graph_data = this.dataset;
    if (this.dataset != null) {
      this.reload_graph();
    }
  },
  watch: {
    dataset: {
      immediate: false,
      handler: function () {
        this.graph_data = this.dataset;
        if (this.dataset != null) {
          this.reload_graph();
        }
      },
      deep: true,
    },
  },
  computed: {
    graph_options: function () {
      let dataset = Array.isArray(this.graph_data) ? this.graph_data : [];
      let dataCategory = [];
      let unitsData = [];
      let cpuData = [];
      let dataSeries = ["Total Units", "Cost Per Unit"];

      if (dataset.length > 0) {
        dataSeries = dataset[0].slice(1);

        for (let i = 1; i < dataset.length; i++) {
          dataCategory.push(dataset[i][0]);
          unitsData.push(dataset[i][1]);
          cpuData.push(dataset[i][2]);
        }
      }

      return {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          borderWidth: 0,
          textStyle: {
            color: "#ffffff",
            fontFamily: "Poppins",
            fontSize: 13,
          },
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#475569",
            },
          },
        },
        color: [PERFORMANCE_CHART_COLORS[0], PERFORMANCE_CHART_COLORS[2]],
        legend: {
          data: dataSeries,
          type: "scroll",
          top: 0,
          itemHeight: 10,
          itemWidth: 18,
          textStyle: PERFORMANCE_LEGEND_TEXT,
        },
        grid: {
          top: 44,
          left: "4%",
          right: "5%",
          bottom: "6%",
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: dataCategory,
            axisLine: PERFORMANCE_AXIS_LINE,
            axisTick: {
              alignWithLabel: true,
              lineStyle: {
                color: "#cbd5e1",
              },
            },
            axisLabel: PERFORMANCE_AXIS_LABEL,
          },
        ],
        yAxis: [
          {
            name: "Total Units",
            nameTextStyle: PERFORMANCE_AXIS_NAME,
            type: "value",
            alignTicks: true,
            axisLine: PERFORMANCE_AXIS_LINE,
            splitLine: PERFORMANCE_SPLIT_LINE,
            axisLabel: {
              ...PERFORMANCE_AXIS_LABEL,
              formatter: function (value) {
                if (value >= 1e9) return (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
                if (value >= 1e6) return (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
                if (value >= 1e3) return (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
                return value.toLocaleString("en-CA");
              },
            },
          },
          {
            name: "Cost Per Unit ($)",
            nameTextStyle: PERFORMANCE_AXIS_NAME,
            type: "value",
            alignTicks: true,
            axisLine: PERFORMANCE_AXIS_LINE,
            splitLine: { show: false },
            axisLabel: {
              ...PERFORMANCE_AXIS_LABEL,
              formatter: function (value) {
                if (value >= 1e9) return "$" + (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
                if (value >= 1e6) return "$" + (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
                if (value >= 1e3) return "$" + (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
                return "$" + value.toFixed(2);
              },
            },
          },
        ],
        series: [
          {
            name: dataSeries[0],
            type: "bar",
            data: unitsData,
            yAxisIndex: 0,
            emphasis: {
              focus: "series",
            },
          },
          {
            name: dataSeries[1],
            type: "line",
            data: cpuData,
            yAxisIndex: 1,
            lineStyle: {
              width: 6,
            },
            showSymbol: true,
            symbolSize: 22,
            itemStyle: {
              color: PERFORMANCE_CHART_COLORS[2],
            },
            emphasis: {
              focus: "series",
            },
          },
        ],
      };
    },
  },
  methods: {
    reload_graph: function () {
      this.graph_visible = false;
      setTimeout(() => (this.graph_visible = true), 10);
    },
  },
};
</script>

<style scoped>
.report-subtitle {
  font-family: "Poppins";
  font-weight: 600;
  font-size: 1.05em;
  color: #1e293b;
}

.performanceChart {
  height: 100%;
  width: 100%;
}
</style>
